import { Chart, LegendItem, Plugin } from "chart.js";
import { RefObject } from "react";
import { createRoot } from "react-dom/client";

import { AnalysisDataType } from "@/constants";

const ReactLegendPlugin: Plugin<"line", ReactLegendPluginOptions> = {
  id: "reactLegend",
  afterUpdate(
    chart,
    _args,
    { container, hidden, component: LegendComponent, onClick }
  ) {
    // `container` is a ref rather than the element itself so that it can be
    // dereferenced here, in `afterUpdate`, instead of during React's render
    // pass (React Compiler disallows reading `ref.current` while rendering).
    const containerElement = container?.current;
    if (!containerElement) return;
    const items =
      chart.options?.plugins?.legend?.labels?.generateLabels?.(chart) ||
      // Reuse the built-in legendItems generator if there's not a custom one present
      Chart.defaults.plugins.legend.labels.generateLabels(chart);

    const datasets = chart.data.datasets;

    const renderItems = hidden
      ? items.filter(
          (item) =>
            item.datasetIndex !== undefined &&
            datasets[item.datasetIndex]?.data_type !== undefined &&
            !hidden.includes(
              datasets[item.datasetIndex]?.data_type as AnalysisDataType
            )
        )
      : items;

    if (!chart.legendRoot) {
      chart.legendRoot = createRoot(containerElement);
    }
    chart.legendRoot?.render(
      renderItems.map((item) => (
        <LegendComponent
          item={item}
          chart={chart}
          key={item.text}
          onClick={
            onClick
              ? onClick
              : (_chart, { datasetIndex, hidden }) => {
                  if (datasetIndex !== undefined && hidden !== undefined) {
                    _chart.setDatasetVisibility(datasetIndex, hidden);
                    _chart.update();
                  }
                }
          }
        />
      ))
    );
  },
  defaults: {
    container: null,
  },
};
export default ReactLegendPlugin;

export type ReactLegendPluginOnclick = (chart: Chart, item: LegendItem) => void;

export type ReactLegendComponentProps = {
  chart: Chart;
  item: LegendItem;
  onClick?: ReactLegendPluginOnclick;
};

export type ReactLegendPluginOptions = {
  /**
   * Ref to the element the legend is rendered into. Deliberately a ref and not
   * the element, so `afterUpdate` can read it outside of React's render pass.
   */
  container: RefObject<HTMLElement | null> | null;
  component: React.FunctionComponent<ReactLegendComponentProps>;
  hidden?: AnalysisDataType[];
  onClick?: ReactLegendPluginOnclick;
};
